import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, Heart, Handshake, Calendar, Star, Music, 
  ArrowRight, MapPin, Sparkles, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SectionHeading from '../components/SectionHeading';
import CowboyBoot from '../components/icons/CowboyBoot';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Values data for the cards section
const valuesData = [
  {
    icon: Users,
    title: "Connecting Dancers",
    description: "Bringing people from different clubs, communities and backgrounds together through shared events, activities and experiences."
  },
  {
    icon: Handshake,
    title: "Supporting Clubs & Instructors",
    description: "Encouraging collaboration, sharing knowledge, resources and practical tools, and offering help wherever we can."
  },
  {
    icon: Heart,
    title: "Strengthening Communities",
    description: "Supporting local charities, fundraising initiatives, outreach projects and worthwhile causes through the power of dance."
  },
  {
    icon: Sparkles,
    title: "Promoting Wellbeing",
    description: "Encouraging movement, confidence, friendship and positive social connections in welcoming and inclusive environments."
  },
  {
    icon: Calendar,
    title: "Creating Opportunities",
    description: "Working together to develop new activities, events and experiences that help more people discover and enjoy line dancing."
  },
  {
    icon: Music,
    title: "Celebrating Line Dancing",
    description: "Championing the music, fun, friendship and sense of belonging that make our dance community so special."
  }
];

export default function AboutUsPage() {
  // SEO - Set page title and meta description
  useEffect(() => {
    document.title = 'About Us | Line Dance Central CIC';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Discover the story behind Line Dance Central CIC. Connecting dancers, supporting clubs and strengthening communities through line dancing, collaboration, fundraising and community outreach.';
    
    return () => {
      document.title = 'Line Dance Central';
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-us-page">
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/69d5b3d6974a81b325f7be54715f05e8615ab4e6bce14d9ce2dd710949032bc7.png"
            alt="Line dancers in synchronized formation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-violet-900/90 via-violet-800/85 to-purple-900/80" />
        </div>
        
        {/* Content */}
        <div className="relative container mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex justify-center mb-6">
              <CowboyBoot className="w-12 h-12 text-violet-300" />
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              About Line Dance Central CIC
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl sm:text-2xl text-violet-200 font-medium mb-4"
            >
              Connecting Dancers. Supporting Clubs. Strengthening Communities.
            </motion.p>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-violet-100/80"
            >
              More Than Just Dancing. It's About Community.
            </motion.p>
          </motion.div>
        </div>
        
        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f8fafc"/>
          </svg>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading
              as="h2"
              align="center"
              title="A Shared Passion. A Bigger Purpose."
              className="mb-8"
              titleClassName="text-3xl lg:text-4xl"
            />
            
            <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
              <p>
                At Line Dance Central CIC, we believe line dancing is about so much more than learning steps to music. 
                It's about friendship, laughter, confidence, connection and bringing people together.
              </p>
              <p>
                What began as a shared passion for line dancing has grown into something much bigger — a community-driven 
                organisation dedicated to making a positive difference, both on and off the dance floor.
              </p>
              <p>
                We're passionate about sharing the joy of line dancing, bringing clubs and dancers closer together, 
                creating opportunities for people to connect and using something we love to make a real difference in our communities.
              </p>
            </div>
            
            {/* Highlight Quote */}
            <div className="mt-10 p-8 bg-violet-50 rounded-2xl border border-violet-100">
              <blockquote className="text-xl lg:text-2xl font-medium text-violet-900 text-center italic">
                "Because to us, line dancing isn't just a hobby. It's a community, a passion and a way of bringing people together."
              </blockquote>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <motion.div 
              className="relative order-2 lg:order-1"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/ff49b4127a0591960b40099bf65a8c4810d0a523c4be0dd2d50acb65d4a3b422.png"
                  alt="Happy group at country line dance social event"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-violet-100 rounded-xl -z-10" />
            </motion.div>
            
            {/* Content */}
            <motion.div 
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <SectionHeading
                as="h2"
                title="Our Story — How It All Began"
                className="mb-8"
                titleClassName="text-3xl lg:text-4xl"
              />
              
              <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
                <p>
                  Our journey started on the dance floor, running regular line dance classes and social events 
                  that brought people together through music, movement and friendship.
                </p>
                <p>
                  But it wasn't long before we realised that line dancing could offer so much more. 
                  Alongside our classes and socials, we became increasingly involved in community outreach, 
                  fundraising activities and events supporting local charities and worthwhile causes.
                </p>
                <p>
                  We saw first-hand how something as simple as dancing could bring a smile to someone's face, 
                  build confidence, tackle isolation and create a genuine sense of belonging.
                </p>
                <p>
                  Becoming a Community Interest Company gave us a stronger foundation for our community-focused work, 
                  opening doors to new partnerships, funding opportunities and collaboration. It allowed us to build 
                  on everything we'd already achieved while creating exciting possibilities for the future.
                </p>
              </div>
              
              {/* Highlight */}
              <div className="mt-8 inline-block">
                <span className="text-lg font-semibold text-violet-600 bg-violet-50 px-4 py-2 rounded-full">
                  The Same Passion. Bigger Possibilities.
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Connecting Clubs Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-violet-50 to-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div 
            className="max-w-4xl mx-auto text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bringing Clubs Together, Building a Stronger Dance Community
            </h2>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
                <p>
                  We believe the line dance community is at its best when we work together. Every club, 
                  instructor and organiser brings something special to the dance floor, and we want to celebrate that.
                </p>
                <p>
                  Rather than working in isolation, we believe there's so much to gain from sharing experiences, 
                  exchanging ideas and supporting one another. One of our key ambitions is to help bridge the gap 
                  between clubs, encouraging greater collaboration, stronger relationships and more opportunities 
                  for dancers to come together.
                </p>
                <p>
                  That might mean bringing dancers from different clubs together at social events, sharing useful 
                  resources and practical tools, exchanging ideas, offering advice or simply lending a helping hand 
                  when it's needed.
                </p>
              </div>
              
              {/* Highlight Quote */}
              <div className="mt-8 p-6 bg-white rounded-xl shadow-md border-l-4 border-violet-500">
                <blockquote className="text-lg font-medium text-slate-800 italic">
                  "We're not here to replace clubs or compete with them. We're here to connect, encourage and support them."
                </blockquote>
              </div>
              
              <p className="mt-6 text-slate-600">
                Because at the end of the day, it doesn't matter which club you dance with, where you learned 
                your first steps or how long you've been dancing. We're all part of the same line dance family. 
                And together, we can achieve so much more.
              </p>
            </motion.div>
            
            {/* Image */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1504609813442-a8924e83f76e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxsaW5lJTIwZGFuY2luZyUyMGdyb3VwJTIwZnJpZW5kcyUyMGNvbW11bml0eXxlbnwwfHx8fDE3OTE0NTYzNTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Group of people dancing together"
                  className="w-full h-80 lg:h-96 object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-violet-200 rounded-xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Community Outreach Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1761250027507-c0be614c0254?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBjaGFyaXR5JTIwdm9sdW50ZWVycyUyMGZ1bmRyYWlzaW5nJTIwZXZlbnR8ZW58MHx8fHwxNzkxNDU2MzY0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Community gathering at an event"
                  className="w-full h-80 lg:h-96 object-cover"
                />
              </div>
            </motion.div>
            
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Growing Together, Giving Back
              </h2>
              
              <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
                <p>
                  Community has always been at the heart of what we do. From our earliest days, we've believed 
                  that line dancing can be a wonderful way to give something back.
                </p>
                <p>
                  Through fundraising events, charitable activities and community outreach, we've been able to 
                  use our love of dancing to support local causes and bring enjoyment to people who might not 
                  otherwise have the opportunity to experience it.
                </p>
                <p>
                  As a Community Interest Company, we're committed to building on this work and finding new 
                  ways to make a positive difference. Our structure enables us to explore grants, partnerships 
                  and other funding opportunities that can help us reach more people and develop new community projects.
                </p>
                <p>
                  But for us, it's never just about raising money. It's about creating connections, encouraging 
                  participation, supporting wellbeing and making people feel part of something special.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Roots Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <MapPin className="w-12 h-12 text-violet-500" />
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Rooted in the Heart of the UK
            </h2>
            
            <div className="prose prose-lg max-w-none text-slate-600 space-y-4 text-left lg:text-center">
              <p>
                Our name reflects both where we come from and where we want to go. Based in the central part 
                of the UK, Line Dance Central celebrates our roots while embracing opportunities to connect 
                with dancers, clubs, organisations and communities further afield.
              </p>
              <p>
                We're proud of our local connections, but our enthusiasm for line dancing doesn't stop at 
                geographical boundaries. From community events and social evenings to larger gatherings and 
                line dance weekends, we're passionate about creating enjoyable experiences that bring people 
                together, wherever they're from.
              </p>
              <p>
                We want Line Dance Central to become a familiar, welcoming name within the wider line dance 
                community — somewhere people can connect, share ideas, find support and discover new opportunities.
              </p>
            </div>
            
            {/* Highlight */}
            <div className="mt-10 inline-block">
              <span className="text-xl font-bold text-violet-600">
                One Community. Many Clubs. Endless Possibilities.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dancing With Purpose — What We Believe In
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Line dancing is at the heart of everything we do, but people are the reason we do it. 
              We believe everyone should have the opportunity to enjoy the physical, mental and social 
              benefits of dancing, regardless of their age, background or experience.
            </p>
          </motion.div>
          
          {/* Values Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {valuesData.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <value.icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {value.title}
                    </h3>
                    <p className="text-slate-600">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Future Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-violet-50">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The Same Passion, Bigger Possibilities
            </h2>
            
            <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
              <p>
                Although our journey has taken us from running local classes and socials to building a wider 
                community organisation, our enthusiasm for line dancing has never changed. We're a friendly, 
                dedicated team of people who genuinely love what we do and believe in the positive impact 
                line dancing can have.
              </p>
              <p>
                We're not just interested in filling dance floors. We want to build friendships, strengthen 
                communities, support good causes, encourage clubs to work together and create opportunities 
                that make a lasting difference.
              </p>
              <p>
                We know we can't do everything on our own, and we wouldn't want to. The real strength of the 
                line dance community lies in the people who make it what it is — the dancers, instructors, 
                organisers, volunteers and supporters who give their time, energy and enthusiasm to something they love.
              </p>
              <p>
                By bringing those people together, sharing ideas and supporting each other, we believe we can 
                achieve something truly special. And while we're proud of how far we've come, we're even more 
                excited about what lies ahead.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Come and Be Part of It!
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-4">
              Whether you're a dancer looking for your next great event, an instructor or club interested 
              in working together, a community organisation seeking support, or a charity looking for a 
              creative way to raise funds, we'd love to hear from you.
            </p>
            <p className="text-slate-600 max-w-2xl mx-auto mb-8">
              You don't have to belong to a particular club, and you certainly don't need to be an experienced 
              dancer. All you need is a little enthusiasm and a willingness to be part of something positive. 
              There are so many ways to get involved, and you don't even need a pair of cowboy boots to start!
            </p>
            
            {/* Highlight */}
            <div className="mb-12 p-6 bg-violet-50 rounded-2xl inline-block">
              <p className="text-xl font-semibold text-violet-800">
                Let's connect, collaborate and keep our line dance community moving forward — together.
              </p>
            </div>
          </motion.div>
          
          {/* CTA Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {/* For Dancers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-violet-200 bg-gradient-to-b from-white to-violet-50">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CowboyBoot className="w-7 h-7 text-violet-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    For Dancers
                  </h3>
                  <p className="text-sm text-violet-600 font-medium mb-3">Explore our events</p>
                  <p className="text-slate-600 mb-6">
                    Discover opportunities to dance, meet new people and enjoy fantastic experiences.
                  </p>
                  <Link to="/events">
                    <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white">
                      Explore Events
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
            
            {/* For Clubs & Instructors */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-violet-200 bg-gradient-to-b from-white to-violet-50">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Handshake className="w-7 h-7 text-violet-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    For Clubs & Instructors
                  </h3>
                  <p className="text-sm text-violet-600 font-medium mb-3">Let's work together</p>
                  <p className="text-slate-600 mb-6">
                    Connect with us to share ideas, collaborate on events and strengthen our dance community.
                  </p>
                  <a href="mailto:hi@linedancecentral.uk">
                    <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white">
                      Work With Us
                      <MessageCircle className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
            
            {/* For Charities & Communities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-violet-200 bg-gradient-to-b from-white to-violet-50 sm:col-span-2 lg:col-span-1">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-7 h-7 text-violet-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    For Charities & Communities
                  </h3>
                  <p className="text-sm text-violet-600 font-medium mb-3">Partner with us</p>
                  <p className="text-slate-600 mb-6">
                    Explore how we can work together to support worthwhile causes and community initiatives.
                  </p>
                  <a href="mailto:hi@linedancecentral.uk">
                    <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white">
                      Get in Touch
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing Brand Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-violet-600 to-violet-700">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dancer-community/artifacts/buaegm7r_1D08A5CF-84D3-4ADA-9A3D-0F8EBE77862A.png" 
                alt="Line Dance Central Logo"
                className="w-32 h-32 object-contain"
              />
            </div>
            
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Line Dance Central CIC
            </h2>
            <p className="text-lg text-violet-200 mb-2">
              Connecting Dancers. Supporting Clubs. Strengthening Communities.
            </p>
            <p className="text-violet-100/80 italic">
              Bringing people together, one step at a time.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
